import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, X } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NationalLocalFunnel from '../../components/national/NationalLocalFunnel';
import NationalRelatedGuides from '../../components/national/NationalRelatedGuides';

const SITE = 'https://buildpilot.com.au';
const CANONICAL = `${SITE}/knockdown-rebuild-explained`;

const STEPS = [
  { n: 1, title: 'Check the block is worth keeping', body: 'KDR makes sense when you love the location but the house is past saving. Confirm zoning, overlays and that a new home is permissible.' },
  { n: 2, title: 'Design and choose a builder', body: 'Pick a builder experienced in knockdown rebuilds - demolition coordination and established-suburb site works are their own skill set.' },
  { n: 3, title: 'Demolition and site prep', body: 'Disconnect services, remove asbestos if present, demolish and clear. Typically $20k - $45k depending on size, access and materials.' },
  { n: 4, title: 'Council approval and engineering', body: 'Planning and building approvals, soil tests and engineering for the new home. Established suburbs can mean tighter overlays.' },
  { n: 5, title: 'Build the new home', body: 'Slab to handover, usually 10 - 14 months. Total KDR timelines run 12 - 20 months once demolition and approvals are counted.' },
];

const WORTH = ['You love the street, schools and neighbours', 'The existing home is small, tired or structurally poor', 'A renovation would cost most of a rebuild anyway', 'No stamp duty on land you already own'];
const NOT_WORTH = ['The home has good bones and only needs cosmetic work', 'The block has heavy overlays that limit a new build', "You can't live elsewhere for 12 - 20 months", 'Heritage listing prevents demolition'];

const LOCAL = [
  { code: 'SA', state: 'South Australia', city: 'Adelaide', primary: { label: 'Adelaide knockdown rebuild', to: '/knockdown-rebuild' }, secondary: { label: 'Local KDR detail', to: '/knockdown-rebuild/costs' } },
  { code: 'VIC', state: 'Victoria', city: 'Melbourne', primary: { label: 'Melbourne knockdown rebuild', to: '/knockdown-rebuild-melbourne' }, secondary: { label: 'Local KDR detail', to: '/what-is-a-knockdown-rebuild-melbourne' } },
];

const KnockdownRebuildExplainedPage = () => {
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: `${SITE}/guides` },
      { '@type': 'ListItem', position: 3, name: 'Knockdown rebuild explained', item: CANONICAL },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Knockdown Rebuild Explained: Process, Costs & Is It Worth It? (Australia 2026) | BuildPilot</title>
        <meta name="description" content="What a knockdown rebuild is, the step-by-step process, timelines, demolition costs and when KDR beats renovating - a national guide, with live local builders for your state." />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content="Knockdown Rebuild Explained (Australia 2026)" />
        <meta property="og:description" content="Process, timelines, demolition costs and when a knockdown rebuild beats renovating - then live local builders." />
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <Header variant="light" />

      <main data-testid="national-kdr-page">
        <section className="border-b border-gray-100 bg-[#fafbfc]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe] mb-3">National guide · Australia</p>
            <h1 className="text-[32px] sm:text-[44px] font-bold text-[#1d1d1f] tracking-tight leading-[1.1] mb-4">
              Knockdown rebuild, explained
            </h1>
            <p className="text-[16px] sm:text-[18px] text-[#6b7078] leading-[1.6] max-w-2xl">
              Keep the location you love, replace the house that no longer works. Here's what a knockdown rebuild (KDR) actually involves across Australia - the process, the timeline, the costs, and when it beats renovating.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h2 className="text-[24px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-tight mb-6">The process, step by step</h2>
          <ol className="space-y-5">
            {STEPS.map((s) => (
              <li key={s.n} className="flex gap-4" data-testid={`kdr-step-${s.n}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full gradient-blue-bg text-white text-[15px] font-bold">{s.n}</span>
                <div className="pt-0.5">
                  <h3 className="text-[18px] sm:text-[20px] font-bold text-[#1d1d1f] mb-1">{s.title}</h3>
                  <p className="text-[15px] text-[#6b7078] leading-[1.6]">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="bg-[#fafbfc] border-y border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <h2 className="text-[24px] sm:text-[30px] font-bold text-[#1d1d1f] tracking-tight mb-6">Is a knockdown rebuild worth it?</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="kdr-worth">
                <p className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#00a550] mb-3">KDR tends to win when</p>
                <ul className="space-y-2.5">
                  {WORTH.map((w) => (
                    <li key={w} className="flex items-start gap-2.5 text-[14.5px] text-[#1d1d1f] leading-[1.5]">
                      <Check className="w-4 h-4 text-[#00a550] mt-0.5 shrink-0" strokeWidth={2.5} />{w}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="kdr-not-worth">
                <p className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#d14343] mb-3">Renovate instead when</p>
                <ul className="space-y-2.5">
                  {NOT_WORTH.map((w) => (
                    <li key={w} className="flex items-start gap-2.5 text-[14.5px] text-[#1d1d1f] leading-[1.5]">
                      <X className="w-4 h-4 text-[#d14343] mt-0.5 shrink-0" strokeWidth={2.5} />{w}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/renovation-costs-australia" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1d8efe] hover:underline" data-testid="kdr-to-reno-link">
                Compare with renovation costs in Australia <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        <NationalLocalFunnel
          funnelTestId="kdr-local-funnel"
          cardPrefix="kdr-local-"
          eyebrow="KDR builders in your state"
          heading="Ready to explore it on your block?"
          intro="Our knockdown-rebuild guidance and builder matching are live in South Australia and Victoria, and expanding across Australia. Open your state for local costs, council process and KDR-experienced builders."
          states={LOCAL}
          crossLink={{ label: 'New to building? See how to build a home, step by step', to: '/how-to-build-a-home', testid: 'kdr-to-how-link' }}
        />

        <NationalRelatedGuides currentSlug="/knockdown-rebuild-explained" />
      </main>

      <Footer />
    </div>
  );
};

export default KnockdownRebuildExplainedPage;
